import {
  Box,
  Card,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import strings from "global/constants/StringConstants";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import { useAppSelector } from "hooks/use-app-select";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { hasAccessTo } from "layouts/Config";
import DropzoneBody, {
  dropzoneSurfaceSx,
} from "global/components/CustomDropzone/DropzoneBody";
import { dropzoneFormatsTextShort } from "global/components/CustomDropzone/CustomDropzone.tokens";
import { useAppDispatch } from "redux/hooks";
import { addSigner, uploadFiles } from "screens/SignRequest/SignRequestHelper";
import {
  getEnvelopeId,
  getUploadDocumentLimit,
} from "screens/SignRequest/SignRequestService";
import history from "utils/history";
import { RootState } from "utils/store";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  setErrors,
  setReduxSelectedParticipant,
  setReduxSignRequest,
} from "redux/appSlice";
import { selectEmail, selectUserName } from "redux/authSlice";
import viewpaths from "global/constants/ViewPathConstants";
import type { SvgIconComponent } from "@mui/icons-material";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { getReuestData } from "./DashboardService";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import DashboardRenewableChart from "./DashboardRenewableChart";
import DashboardRecentActivity from "./DashboardRecentActivity";
import DashboardContractSearchCard from "./DashboardContractSearchCard";
import {
  dashFont,
  dashRef,
  dashSize,
  statAccents,
} from "./DashboardReferenceStyles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CustomDropdownButton from "global/components/CustomDropdownButton/CustomDropdownButton";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  BulkSendIcon,
  MeAndOthersIcon,
  OnlySignerIcon,
} from "global/components/RequestTypeIcons";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import UpgradeModal from "global/components/UpgradeModal/UpgradeModal";
import MaintenanceBanner from "global/components/MaintenanceBanner/MaintenanceBanner";
import { centeredPageGutterSx } from "layouts/CenteredAuthenticated/CenteredPage.tokens";
import { tourAttr } from "tour";
import { TOUR_MODAL_CLASS } from "tour/runtime/constants";
import { hasBlockingDialog } from "tour/runtime/tour-gating";
import DashboardPromoStrip from "./DashboardPromoStrip";
import DashboardPromoPopup from "./DashboardPromoPopup";
import {
  dismissPromo,
  isPromoDismissed,
  openPromoNotifyPopup,
  promoOverrideSearch,
} from "helpers/promotionHelpers";
import usePromoCampaign from "hooks/use-promo-campaign";

/** How long the dashboard settles before the promo popup may open. */
const PROMO_POPUP_DELAY_MS = 700;

/** True while another dialog or the product tour already owns the screen. */
const isPromoPopupBlocked = () =>
  hasBlockingDialog() ||
  Boolean(
    document.querySelector(`.${TOUR_MODAL_CLASS}, .react-joyride__overlay`),
  );

interface DashboardStat {
  label: string;
  /** Key on the request-summary payload; keeps the lookup off the label text. */
  dataKey: string;
  subLabel: string;
  /** Used in place of `subLabel` when the count is zero. Optional — only the
   *  cards whose sub-line would read wrong at zero define it. */
  zeroSubLabel?: string;
  icon: SvgIconComponent;
  accent: string;
  /** Sub-label color: a text-contrast-safe shade of `accent` (WCAG 1.4.3). */
  text: string;
  tint: string;
}

// Stat cards, in reference order.
const statsData: DashboardStat[] = [
  {
    label: "Action required",
    dataKey: "action required",
    subLabel: "Needs your signature",
    icon: EditOutlinedIcon,
    ...statAccents.actionRequired,
  },
  {
    label: "Waiting for others",
    dataKey: "waiting for others",
    // Reference reads "All caught up" against a zero count; saying that with
    // requests outstanding would be wrong, so it swaps once there are any.
    subLabel: "Awaiting signatures",
    zeroSubLabel: "All caught up",
    icon: ScheduleOutlinedIcon,
    ...statAccents.waitingForOthers,
  },
  {
    label: "Expiring soon",
    dataKey: "Pending",
    subLabel: "All time",
    icon: HourglassEmptyOutlinedIcon,
    ...statAccents.expiringSoon,
  },
  {
    label: "Completed",
    dataKey: "Signed",
    subLabel: "All time",
    icon: CheckCircleOutlineOutlinedIcon,
    ...statAccents.completed,
  },
];

const Dashboard = () => {
  useTitle(title.DASHBOARD);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { signRequest, isRequest, uploadedFileNames } = useAppSelector(
    (state: RootState) => state.app,
  );
  // Create Request lives in the side nav, which only exists from `lg` up — so
  // every width below that needs the dashboard's own copy. This used to read
  // the redux `isMobile` flag, but that is only recomputed on a real window
  // resize, so the button was missing whenever the viewport started wide.
  const showCreateRequest = useMediaQuery(theme.breakpoints.down("lg"));
  // Phone column of the reference (375/390) vs its tablet column (834). The
  // two lay the greeting, the create button and the stat cards out differently,
  // so the split has to be a real breakpoint too.
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [loader, setLoader] = useState<boolean>(false);
  const [docLimit, setDocLimit] = useState(0);
  const userName = useAppSelector(selectUserName);
  const userAccess =
    hasAccessTo(strings.resources.sign, strings.sendPermission) ||
    hasAccessTo(strings.resources.template, strings.addPermission);
  const [requestData, setRequestData] = useState<any>(null);
  const userEmail: string = useAppSelector(selectEmail);

  const bulkSendState = useFeatureState(FeatureKeys.ENVELOPE_BULK_SEND);
  const isBulkSendAvailable =
    bulkSendState.status === strings.FEATURE_STATUS_AVAILABLE;
  const bulkSendTooltip = isBulkSendAvailable
    ? ""
    : bulkSendState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((bulkSendState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : strings.FEATURE_GATE_UPSELL_TOOLTIP;

  const multiDocState = useFeatureState(FeatureKeys.ENVELOPE_MULTI_DOCUMENT);
  const isMultiDocAvailable =
    multiDocState.status === strings.FEATURE_STATUS_AVAILABLE;
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState<string | undefined>();

  // Seasonal promotion — phase, campaign and who may see it all come from
  // `usePromoCampaign` (no promotions API exists yet).
  const location = useLocation();
  const {
    phase: promoPhase,
    campaign: promoCampaign,
    finalDays: isPromoFinalDays,
    isEligible: isPromoEligible,
  } = usePromoCampaign();
  // Dismissals made during this visit; `isPromoDismissed` covers earlier ones.
  const [promoDismissed, setPromoDismissed] = useState<Record<string, boolean>>(
    {},
  );
  const [promoPopupOpen, setPromoPopupOpen] = useState(false);

  const stripKey = `strip.${promoPhase}`;
  const showPromoStrip =
    isPromoEligible &&
    promoPhase !== "none" &&
    !promoDismissed[stripKey] &&
    !isPromoDismissed("strip", promoPhase, userEmail);

  // The popup is once per campaign, and the teaser has no offer to show yet.
  useEffect(() => {
    setPromoPopupOpen(false);
    if (!promoCampaign || promoPhase === "teaser" || !isPromoEligible) return;
    if (isPromoDismissed("popup", promoCampaign, userEmail)) return;
    const timer = window.setTimeout(() => {
      // Never stack on another dialog or the tour; it gets the next visit.
      if (!isPromoPopupBlocked()) setPromoPopupOpen(true);
    }, PROMO_POPUP_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [promoCampaign, promoPhase, isPromoEligible, userEmail]);

  const openPromoOffer = () =>
    history.push({
      pathname: viewpaths.viewMyPlanViewPath,
      search: promoOverrideSearch(location.search),
      state: { upgradePlan: true },
    });

  const dismissPromoStrip = () => {
    setPromoDismissed((prev) => ({ ...prev, [stripKey]: true }));
    dismissPromo("strip", promoPhase, userEmail);
  };

  const closePromoPopup = () => {
    setPromoPopupOpen(false);
    if (promoCampaign) dismissPromo("popup", promoCampaign, userEmail);
  };

  const triggerMultiDocGate = () => {
    if (multiDocState.status === strings.FEATURE_STATUS_NOT_IN_PLAN) {
      setUpgradeOpen(true);
    } else {
      setMaintenanceMsg((multiDocState as any).disabledMessage);
      setMaintenanceOpen(true);
    }
  };

  // Create Request, at the two sizes the reference draws it: a full-bleed bar
  // on the phone (main half stretches, 44px caret) and a hug-content button on
  // the tablet. Only geometry is overridden — fill, radius and weight still
  // come from `palette.splitButton`.
  const mobileButtonSx = showCreateRequest
    ? {
        "&.MuiButtonBase-root": {
          minWidth: "unset !important",
          minHeight: "unset !important",
          height: "auto !important",
          flexGrow: { xs: 1, sm: 0 },
          justifyContent: "center",
          padding: { xs: "13px 14px", sm: "12px 18px" },
          fontSize: { xs: "0.875rem", sm: "0.84375rem" },
        },
      }
    : {};

  const handleRequestTypeSelect = (type: string) => {
    if (type === "signer") {
      const selfSigner = {
        id: 0,
        name: userName,
        email: userEmail,
        order: 0,
        color: "",
        cc: false,
      };
      addSigner(selfSigner, [], dispatch, false);
      dispatch(setReduxSelectedParticipant(selfSigner));
      history.push(viewpaths.createSelfSignRequestPath);
      return;
    }

    history.push(`${viewpaths.createSignRequestsViewPath}?type=${type}`);
  };

  const showDashboardActivity = hasAccessTo(
    strings.resources.sign,
    strings.fetchPermission,
  );
  const showDashboardRenewable = hasAccessTo(
    strings.resources.renewable,
    strings.fetchPermission,
  );
  useEffect(() => {
    uploadDocumentsLimit();
  }, []);

  /*
    The two calls are independent, and they used to be awaited in sequence
    inside one `try`: a failure in the upload-limit call skipped the counts
    entirely, leaving `requestData` null and the four stat figures stuck on
    their loading skeletons for good. `allSettled` lets each land — or fail —
    on its own.
  */
  const uploadDocumentsLimit = async () => {
    const canReadSign = hasAccessTo(
      strings.resources.sign,
      strings.fetchPermission,
    );
    try {
      setLoader(true);
      const [limitResult, requestResult] = await Promise.allSettled([
        getUploadDocumentLimit(),
        canReadSign ? getReuestData() : Promise.resolve(null),
      ]);

      if (limitResult.status === "fulfilled") {
        setDocLimit(limitResult.value);
      }
      if (canReadSign) {
        // `?? {}` so a null/undefined body still resolves the loading state —
        // every figure then reads 0 through `getValueFromRequestData`.
        setRequestData(
          requestResult.status === "fulfilled"
            ? (requestResult.value ?? {})
            : {},
        );
      }

      const failure = [limitResult, requestResult].find(
        (result) => result.status === "rejected",
      ) as PromiseRejectedResult | undefined;
      if (failure) handleErrorMessage(failure.reason);
    } finally {
      setLoader(false);
    }
  };

  const getValueFromRequestData = (dataKey: string): string =>
    String(requestData?.[dataKey] ?? 0);

  const redirectRoleWise = () => {
    if (hasAccessTo(strings.resources.sign, strings.sendPermission)) {
      dispatch(setReduxSignRequest(null));
      history.push({
        pathname: viewpaths.createSignRequestsViewPath,
        search: "?type=others",
        state: { isFileUploaded: false },
      });
    }
  };

  const onAdd = async (files: File[]) => {
    try {
      if (!files || files.length === 0) return;
      if (!isMultiDocAvailable && files.length > 1) {
        triggerMultiDocGate();
        return;
      }
      let envelopeId = signRequest.id;
      if (signRequest.files.length === 0 && !isTruthy(signRequest.id)) {
        const res = await getEnvelopeId();
        envelopeId = res.message;
      }
      redirectRoleWise();
      dispatch(
        setReduxSignRequest({
          ...signRequest,
          id: envelopeId,
        }),
      );
      dispatch(setErrors({}));
      uploadFiles(files, dispatch, isRequest, envelopeId);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  // Greeting block — reference order puts this above the stat cards, with the
  // dropzone below them. No date line, per the reference brief.
  const getGreeting = () => (
    <Stack
      // Phone stacks the create button under the greeting at full width; the
      // tablet reference puts it on the greeting's own row, right-aligned.
      // Above `md` the button lives in the side nav, so the row never applies.
      direction={showCreateRequest ? { xs: "column", sm: "row" } : "column"}
      alignItems={showCreateRequest ? { xs: "stretch", sm: "flex-end" } : undefined}
      justifyContent="space-between"
      spacing={showCreateRequest ? { xs: 2.25, sm: 2.5 } : 3}
    >
      <Stack spacing={0.625} sx={{ minWidth: 0 }}>
        <Typography
          component="h2"
          sx={{
            fontSize: dashFont.pageHeading,
            fontWeight: 800,
            letterSpacing: "-0.02em",
            color: dashRef.heading,
          }}
        >
          Welcome {userName}!{" "}
          <Box component="span" aria-hidden="true">
            👋
          </Box>
        </Typography>
        <Typography
          sx={{ fontSize: dashFont.pageSub, color: dashRef.pageSubText }}
        >
          Here's what needs your attention across your sign requests.
        </Typography>
      </Stack>
      {showCreateRequest && (
        <Box
          sx={{
            display: "flex",
            flexShrink: 0,
            justifyContent: { xs: "flex-start", sm: "flex-end" },
            // The split button hugs its label by default; the phone reference
            // runs it edge to edge, so the whole chain down to the group has
            // to be told to stretch.
            "& > *": { width: { xs: "100%", sm: "auto" } },
            "& .MuiButtonGroup-root": { width: { xs: "100%", sm: "auto" } },
            // `menuPaperSx` pins the dropdown to 220px and wins over the
            // component's `sx` prop, so the reference widths are set from
            // here: full-bleed under the phone button, 290px on the tablet.
            "& .MuiCollapse-root .MuiPaper-root": {
              width: { xs: "100%", sm: 290 },
            },
          }}
        >
          <CustomDropdownButton
            handleMainClick={() => handleRequestTypeSelect("others")}
            startIcon={<AddCircleOutlineRoundedIcon color="inherit" />}
            buttonLabel="Create Request"
            menuItems={[
              {
                icon: (
                  <OnlySignerIcon sx={{ fontSize: 20, color: "#6b7280" }} />
                ),
                label: "I am the only signer",
                onClick: () => handleRequestTypeSelect("signer"),
              },
              {
                icon: (
                  <MeAndOthersIcon sx={{ fontSize: 20, color: "#6b7280" }} />
                ),
                label: "Me and others",
                onClick: () => handleRequestTypeSelect("others"),
              },
              {
                icon: (
                  <BulkSendIcon
                    sx={{
                      fontSize: 20,
                      color: isBulkSendAvailable ? "#6b7280" : "#bdbdbd",
                    }}
                  />
                ),
                label: "Bulk send",
                disabled: !isBulkSendAvailable,
                tooltip: !isBulkSendAvailable ? bulkSendTooltip : undefined,
                endIcon: !isBulkSendAvailable ? (
                  <LockOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                ) : undefined,
                onClick: () => {
                  if (!isBulkSendAvailable) return;
                  history.push(viewpaths.bulkSendViewPath);
                },
              },
            ]}
            buttonGroupSx={{
              ...mobileButtonSx,
            }}
            buttonGroupParentSx={{
              width: "100%",
              display: "flex",
              justifyContent: isPhone ? "flex-start" : "flex-end",
            }}
            arrowButtonSx={
              showCreateRequest
                ? {
                    "&.MuiButtonBase-root": {
                      minHeight: "unset !important",
                      height: "auto !important",
                      width: { xs: 44, sm: 40 },
                      minWidth: { xs: 44, sm: 40 },
                      padding: 0,
                    },
                  }
                : {}
            }
          />
        </Box>
      )}
    </Stack>
  );

  /**
   * Phone-only shortcut from the mobile reference. The stat grid is two
   * columns wide down there, which buries the one figure that needs acting on,
   * so the same count is surfaced above it as a tappable row into the inbox.
   * Hidden from `sm` up, where the tablet/desktop grid already reads clearly.
   */
  const getActionRequiredCallout = () => {
    if (!isPhone || !showDashboardActivity) return null;
    const pending = Number(getValueFromRequestData("action required"));
    if (loader || pending <= 0) return null;

    return (
      <Card
        elevation={0}
        role="button"
        tabIndex={0}
        aria-label={`${pending} sign requests need your signature. Open your inbox.`}
        onClick={() => history.push(viewpaths.inboxViewPath)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            history.push(viewpaths.inboxViewPath);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          padding: "13px 14px",
          borderRadius: "16px",
          border: `1px solid ${dashRef.calloutBorder}`,
          boxShadow: "none",
          cursor: "pointer",
          backgroundColor: "background.paper",
          "&:hover": { borderColor: dashRef.calloutBorderHover },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: "-2px",
          },
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "13px",
            backgroundColor: statAccents.actionRequired.tint,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <EditOutlinedIcon
            sx={{ fontSize: 20, color: statAccents.actionRequired.accent }}
          />
        </Box>
        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: dashFont.rowTitle,
              fontWeight: 800,
              color: dashRef.heading,
            }}
          >
            {`${pending} need your signature`}
          </Typography>
          <Typography
            sx={{
              fontSize: dashFont.cardDescription,
              fontWeight: 600,
              color: dashRef.accentText,
            }}
          >
            Tap to review your inbox
          </Typography>
        </Stack>
        <ChevronRightRoundedIcon
          aria-hidden="true"
          sx={{ fontSize: 16, color: dashRef.chevron, flexShrink: 0 }}
        />
      </Card>
    );
  };

  const getUploadFiles = () => (
    <Box {...tourAttr("dashboard.dropzone")}>
      <CustomDropzone
        innerBody={
          <DropzoneBody
            // The full prompt overflows two lines at 375px, so the phone
            // reference runs a shorter one.
            title={
              isPhone
                ? "Upload a document to start"
                : "Drop a document to start a sign request"
            }
            formatsText={isPhone ? dropzoneFormatsTextShort : undefined}
          />
        }
        sx={dropzoneSurfaceSx({
          disabled:
            signRequest.files.length >= docLimit ||
            !hasAccessTo(strings.resources.sign, strings.sendPermission),
        })}
        onAdd={onAdd}
        maxFileSize={10 * 1024 * 1024}
        multipleFileUpload={isMultiDocAvailable}
        showAlerts={false}
        fileNames={uploadedFileNames}
        disabled={
          !userAccess ||          signRequest.files.length >= docLimit ||
          loader ||
          !hasAccessTo(strings.resources.sign, strings.sendPermission)
        }
        uploadDocLimit={docLimit}
        acceptedFiles={[
          ".jpeg",
          ".jpg",
          ".png",
          ".gif",
          ".bmp",
          ".tiff",
          ".pdf",
          ".doc",
          ".docx",
          ".txt",
          ".xls",
          ".xlsx",
          ".heic",
        ]}
        uniqueId="upload-documents-54345"
        isUploadDocLimit={true}
      />
    </Box>
  );

  // Phone stacks the tile above the figure (reference: 2-up grid of tall
  // cards); tablet and desktop keep the tile beside it.
  const statsCardSx = {
    height: "100%",
    /*
      The app sets no global `border-box`, so `height: 100%` resolved against
      the row *plus* this card's 20px top/bottom padding — every card rendered
      40px taller than the row that held it, swallowing the 32px section gap
      and running 8px into the dropzone below.
    */
    boxSizing: "border-box",
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: "flex-start",
    gap: { xs: 1.125, sm: 2 },
    padding: dashSize.statCardPadding,
    borderRadius: dashSize.statCardRadius,
    border: `1px solid ${dashRef.cardBorder}`,
    boxShadow: dashRef.cardShadow,
    backgroundColor: "background.paper",
  } as const;

  const getDashboardStats = () => {
    const showActivity = hasAccessTo(
      strings.resources.sign,
      strings.fetchPermission,
    );
    return (
      showActivity && (
        <Box
          aria-busy={loader}
          sx={{
            width: "100%",
            display: "grid",
            gap: { xs: 1.5, sm: 1.75, md: 2 },
            /*
              Stretch at every width, not just on the phone. The four labels
              are different lengths ("Waiting for others" wraps where "All
              time" does not), so `start` left each card sized to its own text
              and the row came out ragged. Stretching gives one common height;
              `statsCardSx`'s `height: 100%` then fills it.
            */
            alignItems: "stretch",
            // Reference keeps two columns on the phone as well — the cards go
            // tall rather than full-width there.
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
          }}
        >
          {statsData.map((stat, index) => {
            // Icon, label and sub-label are static per card, so they render
            // straight away — only the figure waits on the API.
            // Only the fetch itself gates the figure now — a failed request
            // resolves to 0 rather than leaving the skeleton up for good.
            const isLoading = loader;
            const value = getValueFromRequestData(stat.dataKey);
            const isZero = !isLoading && Number(value) === 0;
            const subLabel =
              isZero && stat.zeroSubLabel ? stat.zeroSubLabel : stat.subLabel;
            const StatIcon = stat.icon;
            return (
              // The card is the grid item itself: a plain wrapper would be the
              // one that gets stretched, leaving the card's `height: 100%` to
              // resolve against a shrink-wrapped parent.
              <Card key={index} elevation={0} sx={statsCardSx}>
                <Box
                  sx={{
                    width: dashSize.statTile,
                    height: dashSize.statTile,
                    borderRadius: dashSize.statTileRadius,
                    backgroundColor: stat.tint,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <StatIcon
                    sx={{ fontSize: dashSize.statIcon, color: stat.accent }}
                  />
                </Box>
                {/* The reference stacks these three lines 1px apart on the
                    smaller viewports; the theme's 2px step is a desktop
                    figure and is kept from `md` up. */}
                <Stack
                  spacing={{ xs: 0.125, md: 0.25 }}
                  sx={{ minWidth: 0 }}
                >
                  {isLoading ? (
                    <Skeleton
                      variant="rounded"
                      width={44}
                      sx={{
                        // Sized off the figure's own type scale (1.1em of
                        // statValue) so the card keeps its height when the
                        // value lands.
                        fontSize: dashFont.statValue,
                        height: "1.1em",
                        borderRadius: "6px",
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: dashFont.statValue,
                        fontWeight: 800,
                        letterSpacing: "-0.02em",
                        lineHeight: 1.1,
                        color: dashRef.heading,
                      }}
                    >
                      {value}
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      fontSize: dashFont.statLabel,
                      fontWeight: 700,
                      color: dashRef.heading,
                      // The theme's body line-height (1.5) adds ~5px per
                      // line here, which compounds over a 2x2 grid and
                      // pushes the block well past the reference. The
                      // reference leaves these lines at the browser default.
                      lineHeight: dashSize.statTextLineHeight,
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: dashFont.statSub,
                      fontWeight: 600,
                      lineHeight: dashSize.statTextLineHeight,
                      color:
                        isZero && stat.zeroSubLabel
                          ? dashRef.metaText
                          : stat.text,
                    }}
                  >
                    {subLabel}
                  </Typography>
                </Stack>
                {/* Tablet reference closes each card with a chevron. It is
                    decorative — the cards carry no action in the existing
                    implementation — so it is hidden from assistive tech, and
                    it only appears in the two-up band the reference draws
                    (the four-across desktop grid keeps its plain cards). */}
                <ChevronRightRoundedIcon
                  aria-hidden="true"
                  sx={{
                    display: { xs: "none", sm: "block", lg: "none" },
                    ml: "auto",
                    flexShrink: 0,
                    fontSize: 15,
                    color: dashRef.chevron,
                  }}
                />
              </Card>
            );
          })}
        </Box>
      )
    );
  };

  const getActivityAndRenewalsSection = () => {
    const showActivity = showDashboardActivity;
    const showRenewableChart = showDashboardRenewable;

    if (!showActivity && !showRenewableChart) {
      return null;
    }

    const baseSectionPaperSx = {
      borderRadius: "16px",
      overflow: "hidden",
      // Same hairline the stat cards use, so the whole dashboard grid reads
      // as one set of cards.
      border: `1px solid ${dashRef.cardBorder}`,
      boxShadow: "none",
      background:
        theme.palette.mode === "dark"
          ? "linear-gradient(135deg, rgba(30,32,38,0.95) 0%, rgba(22,24,30,0.92) 100%)"
          : "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.88) 100%)",
      backdropFilter: "blur(10px)",
    };

    const cardPadding = dashSize.sectionCardPadding;

    const dualRow = showActivity && showRenewableChart;

    const paperFlexSx = dualRow
      ? {
          width: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column" as const,
          minHeight: { xs: 280, md: 360 },
        }
      : { width: "100%", alignSelf: "flex-start" as const };

    return (
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gap: { xs: 2.25, sm: 2.5, md: 2 },
          // The extra rule above this pair is a desktop device; below `md` the
          // two cards are just the next blocks in the stack.
          mt: { xs: 0, md: 2 },
          alignItems: "stretch",
          /*
            Reference gives recent activity the wider column.

            `minmax(0, 1fr)`, never a bare `1fr`: a bare track's minimum is
            `auto`, so it floors at its content's min-content width and grows
            past the grid instead of shrinking. The renewals chart and the
            activity rows measured 440px against a 382px column on a 414px
            phone, which is where the page-wide horizontal scrollbar came from.
          */
          gridTemplateColumns:
            showActivity && showRenewableChart
              ? { xs: "minmax(0, 1fr)", md: "1.35fr minmax(0, 1fr)" }
              : "minmax(0, 1fr)",
        }}
      >
        {showActivity && (
          <Box
            {...tourAttr("dashboard.recentActivity")}
            sx={{ minWidth: 0, ...(dualRow ? { display: "flex" } : null) }}
          >
            <Paper
              elevation={0}
              sx={{
                ...baseSectionPaperSx,
                ...paperFlexSx,
                p: cardPadding,
              }}
            >
              <DashboardRecentActivity />
            </Paper>
          </Box>
        )}
        {showRenewableChart && (
          <Box
            {...tourAttr("dashboard.renewals")}
            sx={{ minWidth: 0, ...(dualRow ? { display: "flex" } : null) }}
          >
            <Paper
              elevation={0}
              sx={{
                ...baseSectionPaperSx,
                ...paperFlexSx,
                p: cardPadding,
              }}
            >
              <DashboardRenewableChart fillAvailableHeight={dualRow} />
            </Paper>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <CenteredAuthenticatedLayout>
      {/*
        The desktop gutter every centred page shares — see
        `CenteredPage.tokens`. `md`-only, so the phone and tablet keep the
        layout's own gutters.
      */}
      <Stack
        spacing={dashSize.sectionGap}
        justifyContent={"center"}
        // `boxSizing` because the app sets no global `border-box`: without it
        // the gutter below would be added to this `width: 100%` and push the
        // page 80px past the column.
        sx={{ width: "100%", boxSizing: "border-box", ...centeredPageGutterSx }}
      >
        <Typography
          variant="h1"
          sx={{
            position: "absolute",
            width: "1px",
            height: "1px",
            padding: 0,
            margin: "-1px",
            overflow: "hidden",
            clip: "rect(0, 0, 0, 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          Dashboard
        </Typography>
        {showPromoStrip && (
          <DashboardPromoStrip
            phase={promoPhase}
            onViewOffer={openPromoOffer}
            onNotify={openPromoNotifyPopup}
            onDismiss={dismissPromoStrip}
          />
        )}
        {getGreeting()}
        {getActionRequiredCallout()}
        {getDashboardStats()}
        {getUploadFiles()}
        {hasAccessTo(strings.resources.sign, strings.fetchPermission) && (
          <DashboardContractSearchCard />
        )}
        {getActivityAndRenewalsSection()}
      </Stack>
      <UpgradeModal
        open={upgradeOpen}
        featureKey={FeatureKeys.ENVELOPE_MULTI_DOCUMENT}
        onClose={() => setUpgradeOpen(false)}
      />
      {promoCampaign && (
        <DashboardPromoPopup
          open={promoPopupOpen}
          campaign={promoCampaign}
          finalDays={isPromoFinalDays}
          onClaim={() => {
            closePromoPopup();
            openPromoOffer();
          }}
          onClose={closePromoPopup}
        />
      )}
      <MaintenanceBanner
        open={maintenanceOpen}
        message={maintenanceMsg}
        onClose={() => setMaintenanceOpen(false)}
      />
    </CenteredAuthenticatedLayout>
  );
};

export default Dashboard;
